import {
  InfoCircleOutlined,
  ReloadOutlined,
  } from "@ant-design/icons";
import {
  Badge,
  Button,
  DatePicker,
  Empty,
  Flex,
  Input,
  Popconfirm,
  Select,
  Skeleton,
  Space,
  Tooltip,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { logMessageAPIWithErrorHandling } from "../../../api/logMessage/logMessage";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ImportExportMenu from "../../../components/cms/ImportExportMenu";
import ListToolbar from "../../../components/cms/ListToolbar";
import CmsTableCard from "../../../components/cms/CmsTableCard";
import { brand, spacing, typography, semantic } from "../../../theme/tokens";
import StatusTag from "../../../components/cms/StatusTag";

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Nhãn loại tin theo sự kiện (fallback về type)
const TYPE_LABELS = {
    sinh_nhat: "Sinh nhật",
    thong_bao_tich_diem: "Tích điểm",
    nang_hang_thanh_vien: "Nâng hạng",
    tai_khoan_phe_duyet: "Duyệt tài khoản",
    gui_ma_otp: "OTP",
    sms: "SMS",
};

const EVENT_OPTIONS = [
    { value: "sinh_nhat", label: "Sinh nhật" },
    { value: "thong_bao_tich_diem", label: "Tích điểm" },
    { value: "nang_hang_thanh_vien", label: "Nâng hạng" },
    { value: "tai_khoan_phe_duyet", label: "Duyệt tài khoản" },
    { value: "gui_ma_otp", label: "OTP" },
    { value: "sms", label: "SMS thường" },
];

const SEND_STATUS_OPTIONS = [
    { value: "SUCCESS", label: "Thành công" },
    { value: "FAILED", label: "Thất bại" },
    { value: "UNDELIVERED", label: "Không nhận được" },
    { value: "PENDING", label: "Đang gửi" },
];

// Mã code lúc GỬI (đẩy qua hệ thống) -> hiện lý do khi gửi lỗi
const SMS_SEND_CODE = {
    "0": "Success",
    "1": "Lỗi hệ thống",
    "2": "Dữ liệu vào không hợp lệ",
    "3": "Sai username hoặc password",
    "4": "Tài khoản chưa kích hoạt",
    "5": "Số điện thoại không hợp lệ",
    "6": "Phone send sms is loop (đã gửi)",
    "7": "Tài khoản không đủ quota (hết tiền)",
    "8": "Brandname chưa được kích hoạt cho tài khoản",
    "9": "Brandname chưa được kích hoạt bởi nhà mạng",
    "10": "Nhà mạng không khớp",
    "11": "Không tìm thấy IP",
    "13": "Unicode chỉ nhận 0 hoặc 1",
};
const ZNS_SEND_CODE = {
    "0": "Success",
    "6": "Phone send sms is loop (đã gửi)",
    "7": "Tài khoản không đủ quota (hết tiền)",
    "-109": "Template không hợp lệ",
    "-111": "Lỗi template",
    "-114": "Người dùng từ chối / không hoạt động",
};

// Bảng mã dlvr_status (khi lấy BÁO CÁO giao)
const ZNS_DLVR = {
    "0": "Zalo chưa gửi được cho khách hàng",
    "1": "Gửi thành công xuống máy khách hàng",
    "2": "Cannot found referentId",
    "3": "Gửi lặp",
    "-100": "Phát sinh lỗi không xác định",
    "-101": "Ứng dụng gửi ZNS không hợp lệ",
    "-102": "Ứng dụng gửi ZNS không tồn tại",
    "-109": "Template không hợp lệ",
    "-111": "Lỗi template",
    "-114": "Người dùng không hoạt động / từ chối tin nhắn",
};
const SMS_DLVR = { "0": "Gửi tin thất bại", "1": "Gửi tin thành công" };

const FILTER_DEFS = [
    { key: "type", label: "Loại tin" },
    { key: "event", label: "Loại sự kiện" },
    { key: "send_status", label: "Trạng thái" },
    { key: "date", label: "Khoảng ngày" },
    { key: "search", label: "Tìm kiếm" },
];
// const DEFAULT_VISIBLE_FILTERS = ["search", "type", "send_status"];
const DEFAULT_VISIBLE_FILTERS = [];

const COLUMN_DEFS = [
    { key: "index", label: "STT", fixed: true },
    { key: "customer", label: "Khách hàng", fixed: true },
    { key: "referentid", label: "Reference ID" },
    { key: "type", label: "Loại tin" },
    { key: "content", label: "Nội dung" },
    { key: "send_status", label: "Trạng thái" },
    { key: "dlvr", label: "Chi tiết trạng thái" },
    { key: "created_at", label: "Ngày tạo" },
    { key: "operation", label: "Thao tác", fixed: true },
];
const DEFAULT_VISIBLE_COLUMNS = [
    "referentid", "type", "content", "send_status", "created_at",
];

export default function LogMessageHistories() {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [resendingId, setResendingId] = useState(null);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });

    const [filters, setFilters] = useState({
        search: "", type: undefined, event: undefined,
        send_status: undefined, from_date: undefined, to_date: undefined,
    });
    const [visibleFilters, setVisibleFilters] = useState(DEFAULT_VISIBLE_FILTERS);
    const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
    const searchTimer = useRef(null);

    const loadData = useCallback(async (overrides = {}) => {
        const page = overrides.page ?? pagination.current;
        const per_page = overrides.per_page ?? pagination.pageSize;
        const f = { ...filters, ...overrides.filters };
        try {
            setLoading(true);
            const res = await logMessageAPIWithErrorHandling.getHistories({
                page, per_page,
                search: f.search, type: f.type, event: f.event,
                send_status: f.send_status, from_date: f.from_date, to_date: f.to_date,
            });
            if (res.data?.success) {
                const d = res.data.data;
                setData(d.data || []);
                setPagination((p) => ({ ...p, current: d.current_page, total: d.total, pageSize: per_page }));
            }
        } catch (error) {
            message.error(error.message);
        } finally {
            setLoading(false);
        }
    }, [filters, pagination.current, pagination.pageSize]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { loadData({ page: 1 }); }, []);

    // Áp bộ lọc -> về trang 1
    const applyFilter = (patch) => {
        const next = { ...filters, ...patch };
        setFilters(next);
        setPagination((p) => ({ ...p, current: 1 }));
        loadData({ page: 1, filters: patch });
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setFilters((f) => ({ ...f, search: value }));
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            setPagination((p) => ({ ...p, current: 1 }));
            loadData({ page: 1, filters: { search: value } });
        }, 500);
    };

    const handleTableChange = (pg) => {
        setPagination((p) => ({ ...p, current: pg.current, pageSize: pg.pageSize }));
        loadData({ page: pg.current, per_page: pg.pageSize });
    };

    const handleReset = () => {
        const cleared = { search: "", type: undefined, event: undefined, send_status: undefined, from_date: undefined, to_date: undefined };
        setFilters(cleared);
        setPagination((p) => ({ ...p, current: 1 }));
        loadData({ page: 1, filters: cleared });
    };

    const handleResend = async (record) => {
        try {
            setResendingId(record.id);
            const res = await logMessageAPIWithErrorHandling.resend(record.id);
            if (res.data?.success) {
                message.success("Đã đẩy gửi lại tin");
                loadData();
            } else {
                message.error(res.data?.message || "Gửi lại thất bại");
            }
        } catch (error) {
            message.error(error.message);
        } finally {
            setResendingId(null);
        }
    };

    // ===== Render helpers =====
    const renderLoaiTin = (record) => TYPE_LABELS[record?.event] || record?.type || "-";

    const renderSendStatus = (record) => {
        const s = record?.send_status;
        if (s === "SUCCESS") return <Badge status="success" text="Thành công" />;
        if (s === "FAILED") return <Badge status="error" text="Thất bại" />;
        if (s === "UNDELIVERED") return <Badge status="warning" text="Không nhận được" />;
        if (s === "PENDING") return <Badge status="processing" text="Đang gửi" />;
        return <Badge status="default" text="—" />;
    };

    // Chi tiết trạng thái: ưu tiên báo cáo giao (dlvr_status); nếu chưa có + gửi lỗi -> lý do code lúc gửi.
    const renderDetail = (record) => {
        const type = record?.type;
        const dlvr = record?.dlvr_status;

        if (dlvr !== null && dlvr !== undefined && dlvr !== "") {
            const map = type === "ZNS" ? ZNS_DLVR : SMS_DLVR;
            const text = map[String(dlvr)] || "Không xác định";
            let color = "#595959";
            if (String(dlvr) === "1") color = "#52c41a";
            else if (String(dlvr) === "0") color = "#fa8c16";
            else if (["2", "3"].includes(String(dlvr))) color = "#1890ff";
            else if (String(dlvr).startsWith("-")) color = "#ff4d4f";
            return <span style={{ color }}>{`${dlvr} - ${text}`}</span>;
        }

        const code = record?.code;
        const sStatus = record?.send_status;
        if ((sStatus === "FAILED" || sStatus === "UNDELIVERED") && code !== null && code !== undefined && code !== "") {
            const cmap = type === "ZNS" ? ZNS_SEND_CODE : SMS_SEND_CODE;
            return <span style={{ color: semantic.danger }}>{`${code} - ${cmap[String(code)] || "Gửi thất bại"}`}</span>;
        }
        return <Text type="secondary">—</Text>;
    };

    const allColumnsMap = useMemo(() => {
        const map = {};
        map.index = {
            title: "STT", key: "index", width: 60, fixed: "left",
            render: (_, __, i) => (pagination.current - 1) * pagination.pageSize + i + 1,
        };
        map.customer = {
            title: "Khách hàng", dataIndex: "customer", width: 200, fixed: "left",
            render: (_, record) => {
                const c = record?.user?.customer;
                if (!c) return <Text type="secondary">—</Text>;
                return (
                    <Flex align="center" gap={spacing[2]}>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: typography.weight.semibold }}>{c.name}</div>
                            <Text type="secondary" style={{ fontSize: typography.size.sm }}>{c.phone}</Text>
                        </div>
                        <Button
                            type="text" size="small"
                            icon={<InfoCircleOutlined style={{ color: brand.accent }} />}
                            onClick={() => navigate(`/customer-detail/${c.id}`)}
                        />
                    </Flex>
                );
            },
        };
        map.referentid = {
            title: "Reference ID", dataIndex: "referentid", width: 160,
            render: (t) => t || "-",
        };
        map.type = {
            title: "Loại tin", key: "type", width: 130,
            render: (_, r) => {
                const isZns = r?.type === "ZNS";
                return (
                    <Space direction="vertical" size={0}>
                        <StatusTag>
                            {r?.type || "-"}
                        </StatusTag>
                        <Text style={{ fontSize: typography.size.sm }}>{renderLoaiTin(r)}</Text>
                    </Space>
                );
            },
        };
        map.content = {
            title: "Nội dung", dataIndex: "content", width: 280,
            render: (t) => (
                <Tooltip title={t || ""}>
                    <Text style={{ fontSize: typography.size.sm }}>
                        {t ? (t.length > 120 ? t.slice(0, 120) + "…" : t) : "-"}
                    </Text>
                </Tooltip>
            ),
        };
        map.send_status = {
            title: "Trạng thái", key: "send_status", width: 130, align: "center",
            render: (_, r) => renderSendStatus(r),
        };
        map.dlvr = {
            title: "Chi tiết trạng thái", key: "dlvr", width: 240,
            render: (_, r) => renderDetail(r),
        };
        map.created_at = {
            title: "Ngày tạo", dataIndex: "created_at", width: 150, align: "center",
            render: (t) => (t ? dayjs(t).format("DD/MM/YYYY HH:mm:ss") : "—"),
        };
        map.operation = {
            title: "Thao tác", key: "operation", width: 110, align: "center", fixed: "right",
            render: (_, r) =>
                r?.send_status === "FAILED" ? (
                    <Popconfirm title="Gửi lại tin này?" okText="Gửi lại" cancelText="Hủy" onConfirm={() => handleResend(r)}>
                        <Button size="small" danger icon={<ReloadOutlined />} loading={resendingId === r.id}>
                            Gửi lại
                        </Button>
                    </Popconfirm>
                ) : null,
        };
        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.current, pagination.pageSize, resendingId, navigate]);

    const displayColumns = useMemo(
        () => COLUMN_DEFS
            .filter((d) => d.fixed || visibleColumns.includes(d.key))
            .map((d) => allColumnsMap[d.key])
            .filter(Boolean),
        [visibleColumns, allColumnsMap]
    );


    const hasActiveFilter =
        filters.search || filters.type || filters.event || filters.send_status || filters.from_date || filters.to_date;

    return (
        <BrandedPageLayout
            title="Lịch sử gửi tin"
            subtitle="Theo dõi trạng thái gửi ZNS và SMS: đã đẩy qua hệ thống, đã tới máy khách, hay thất bại."
            breadcrumbItems={[{ title: "Lịch sử gửi tin" }]}
        >
            <ListToolbar
                filterDefs={FILTER_DEFS}
                filters={{
                    type: (
                        <Select
                            style={{ width: "100%" }} allowClear placeholder="Loại tin"
                            value={filters.type}
                            onChange={(v) => applyFilter({ type: v })}
                            options={[{ value: "ZNS", label: "ZNS" }, { value: "SMS", label: "SMS" }]}
                        />
                    ),
                    event: (
                        <Select
                            style={{ width: "100%" }} allowClear placeholder="Loại sự kiện"
                            value={filters.event}
                            onChange={(v) => applyFilter({ event: v })}
                            options={EVENT_OPTIONS}
                        />
                    ),
                    send_status: (
                        <Select
                            style={{ width: "100%" }} allowClear placeholder="Trạng thái"
                            value={filters.send_status}
                            onChange={(v) => applyFilter({ send_status: v })}
                            options={SEND_STATUS_OPTIONS}
                        />
                    ),
                    date: (
                        <RangePicker
                            style={{ width: "100%" }} format="DD/MM/YYYY"
                            value={filters.from_date ? [dayjs(filters.from_date), dayjs(filters.to_date)] : null}
                            onChange={(dates) =>
                                applyFilter({
                                    from_date: dates?.[0]?.format("YYYY-MM-DD"),
                                    to_date: dates?.[1]?.format("YYYY-MM-DD"),
                                })
                            }
                        />
                    ),
                    search: (
                        <Input
                            allowClear
                            placeholder="Tìm theo Reference ID / số điện thoại"
                            value={filters.search}
                            onChange={handleSearchChange}
                        />
                    ),
                }}
                onHideFilter={(key) => {
                    if (key === "date") applyFilter({ from_date: undefined, to_date: undefined });
                    else if (key === "search") applyFilter({ search: "" });
                    else applyFilter({ [key]: undefined });
                }}
                onClearFilters={handleReset}
                columnDefs={COLUMN_DEFS}
                visibleColumns={visibleColumns}
                setVisibleColumns={setVisibleColumns}
                defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
                loading={loading}
                onReload={() => loadData()}
                actions={
                    <ImportExportMenu
                        exportUrl="/api/v1/logs/histories/export"
                        exportParams={{
                            ...(filters.search ? { search: filters.search } : {}),
                            ...(filters.type ? { type: filters.type } : {}),
                            ...(filters.event ? { event: filters.event } : {}),
                            ...(filters.send_status ? { send_status: filters.send_status } : {}),
                            ...(filters.from_date ? { from_date: filters.from_date } : {}),
                            ...(filters.to_date ? { to_date: filters.to_date } : {}),
                        }}
                        exportFileName="lich_su_gui_tin.xlsx"
                        exportActions={["logmessage.lichsuguitin"]}
                    />
                }
            />

            <CmsTableCard
                columns={displayColumns}
                dataSource={loading ? [] : data}
                rowKey="id"
                loading={data.length === 0 ? loading : false}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showTotal: (total, range) => `Hiển thị từ ${range[0]} → ${range[1]} trong tổng ${total} giá trị`,
                }}
                onChange={handleTableChange}
                locale={{
                    emptyText: loading ? (
                        <Flex vertical align="center" justify="center" gap={20} style={{ padding: "30px 0" }}>
                            <Skeleton.Input active size="large" style={{ width: 250 }} />
                            <Skeleton.Input active size="small" style={{ width: 150 }} />
                        </Flex>
                    ) : (
                        <Empty description="Không có lịch sử gửi tin" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    ),
                }}
                bordered
                scroll={{ x: "max-content" }}
            />
        </BrandedPageLayout>
    );
}
